import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Lock, Mail, AlertCircle, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { Logo } from "../components/Logo";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  if (process.env.REACT_APP_LOCAL_PREVIEW === 'true' && user) return <Navigate to="/admin" replace />;
  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-stripes opacity-20" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8" data-testid="admin-login-home-link">
          <Logo size={72} />
        </Link>
        <div className="text-center mb-10">
          <div className="font-display text-4xl text-white uppercase">
            Espace <span className="text-[#E87B3A]">admin</span>
          </div>
          <p className="font-body text-white/60 text-sm mt-2">
            Location du Nord — Connexion réservée
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-sm p-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]">Identifiant</label>
            <div className="relative mt-2">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
              <input
                type="text"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="w-full pl-10 pr-3 py-3 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A] focus:ring-1 focus:ring-[#E87B3A]"
              />
            </div>
          </div>
          <div>
            <label className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]">Mot de passe</label>
            <div className="relative mt-2">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
              <input
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="w-full pl-10 pr-12 py-3 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A] focus:ring-1 focus:ring-[#E87B3A]"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                data-testid="admin-login-toggle-password"
                aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#E87B3A] p-1"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-sm" data-testid="admin-login-error">
              <AlertCircle size={18} className="mt-0.5" />
              <span className="font-body text-sm font-bold">{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-[#E87B3A] hover:bg-[#D96C2A] disabled:opacity-60 text-white px-6 py-3.5 rounded-sm font-body font-bold uppercase tracking-wider transition-colors"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>

        <div className="text-center mt-6">
          <Link to="/" className="font-body text-xs uppercase tracking-widest text-white/50 hover:text-[#E87B3A]" data-testid="admin-login-back">
            ← Retour au site
          </Link>
        </div>
      </div>
    </main>
  );
}
